<script lang="ts">
  import { commentBox, keyboard } from '../keys/keyboard.svelte';
  import type { Conversation } from '../state/rangeHost';
  import { clearDraft, loadDraft, mergeText, saveDraft } from '../state/drafts';
  import { onDestroy, onMount, untrack } from 'svelte';
  import type { Anchor } from '../state/anchor';
  import { buildOffDiffBody } from '../logic/offdiff';
  import { session } from '../state/session.svelte';
  import { IDLE, saveOf, tellers, type Save } from '../state/pending.svelte';
  import { focusAt, type FocusAt } from '../logic/focus';
  import { formatSuggestion, parseSuggestion, suggestionLines } from '../logic/suggestion';
  import { applyEdits, editRegions, formatPatch, normalizeEndings, planSuggestion, suggestionComments, type Region, type SuggestPlan } from '../logic/suggestEdit';
  import { textHash } from '../logic/textHash';
  import SaveButton from './SaveButton.svelte';
  import SuggestionEditor from './SuggestionEditor.svelte';
  import SpanQuote from './SpanQuote.svelte';
  import { relayAgents } from '../state/relayAgents.svelte';
  import { THREAD_REQUEST, threadTask } from '../logic/threadTask';
  import { pullKey } from '../api/relaySubject';
  import { THREAD_NEEDS, threadTopic } from '../api/relayTask';
  import { splitSpan, withSpan, type SpanMarker } from '../logic/spanNote';

  interface Props {
    pr: Conversation;
    anchor: Anchor;
    /** Cancel, Close or Esc: nothing of it is waited for any more. */
    onclose: () => void;
    /**
     * Everything it sent is saved, and it gives way to the draft. This comes
     * when the server answers, not at a press: `focus` says where the
     * focus is then, so it is handed on only if the person is still here.
     */
    onsaved: (focus: FocusAt) => void;
  }
  let { pr, anchor, onclose, onsaved }: Props = $props();

  /** The comment text. */
  let text = $state('');
  /**
   * The span of the lines' text the comment is on (logic/spanNote), shown
   * apart, above the text: what is saved and posted starts with its quote
   * and marker (`withSpan`), which the box never shows to be typed over.
   */
  let span = $state<SpanMarker | null>(null);
  /** The whole file as edited for a suggested change, while the suggestion editor is open (one per comment). */
  let edited: string | null = $state(null);
  /** The file at the head a suggestion edits; only a new-side line comment has one. */
  const headFile = $derived(anchor.side === 'RIGHT' && anchor.route === 'line' && anchor.file ? normalizeEndings(anchor.file.lines) : null);
  /** Its lines without CRLF endings (the editor works in `\n`); posted suggestions get the file's ending back. */
  const head = $derived(headFile?.lines ?? null);
  const crlf = $derived(headFile?.crlf ?? false);
  const canSuggest = $derived(head !== null);
  /** Identifies the head's text, so edits saved on another head are not applied to this one. */
  const headHash = $derived(head ? textHash(head.join('\n')) : '');
  const plan: SuggestPlan | null = $derived(edited === null || !head ? null : planSuggestion(head, suggestionLines(edited), anchor.file?.hunks ?? []));
  /** The lines the comment goes on: those a single suggestion replaces, else the selected ones. */
  const target = $derived(plan?.kind === 'one' ? { start: plan.suggestion.start, line: plan.suggestion.end } : { start: anchor.startLine, line: anchor.line });
  /**
   * What is posted (and kept as the draft): the text, then a suggestion
   * block (the selected lines unchanged when nothing is edited yet), or the
   * edits as a patch when they reach outside the diff. Several suggestions
   * are posted one per comment (`save`); their draft is the text.
   */
  const core = $derived.by(() => {
    if (!plan) return text;
    switch (plan.kind) {
      case 'unchanged':
        return formatSuggestion(text, anchor.text);
      case 'one':
        return suggestionComments(plan, text, crlf)[0].body;
      case 'split':
        return text;
      case 'outside':
        return formatPatch(text, plan.patch);
    }
  });
  /** What is posted and kept: the span's quote and marker, if any, then the comment. */
  const body = $derived(withSpan(span, core));
  /** The draft kept for these lines: as `body`, but never a patch block (the edits are kept beside it). */
  const draftBody = $derived(withSpan(span, plan?.kind === 'outside' ? text : core));
  let area: HTMLTextAreaElement | undefined = $state();
  let root: HTMLDivElement | undefined = $state();
  const key = $derived(`comment:${pr.owner}/${pr.repo}#${pr.number}:${anchor.path}:${anchor.side}:${anchor.startLine}-${anchor.line}`);
  /** The boxes it sends from: its own, and one more for each further suggestion (one comment each). */
  const boxes = $derived(plan?.kind === 'split' ? plan.suggestions.map((_, i) => (i === 0 ? key : `${key}:${i + 1}`)) : [key]);
  /**
   * Looking up the commit of off-diff lines, before there is a write: the
   * part of the save the pending writes do not know of.
   */
  let lookup: Save = $state.raw(IDLE);
  /** One comment of a press: its box, its lines and what it says. */
  interface Part {
    box: string;
    start: number;
    line: number;
    body: string;
  }
  /**
   * The comments of a press of several suggestions, from that press until
   * every one is saved. They are fixed then: each is saved or not on its
   * own, Retry sends those not saved, as they were written, and none
   * twice; the text and the edits are not changed meanwhile, since a
   * change could not reach a part the server already has. A part that
   * failed is told here until it is saved, or the composer is closed
   * (then by the toast, as any failure of a closed box). When none of
   * them was saved nothing is fixed: it is null again.
   */
  let parts = $state.raw<readonly Part[] | null>(null);
  /** The boxes of `parts` the server has. */
  let saved = $state.raw<ReadonlySet<string>>(new Set());
  /**
   * How saving stands (see `Writes.box`): idle; saving from the press until
   * every part is answered; or failed, with why. The composer stays open
   * through it and closes once everything is saved.
   */
  const sentFrom = $derived(parts?.map((p) => p.box) ?? boxes);
  // Only what this composer shows (`mine`): a comment on these lines sent by a composer since closed is not its save.
  const saving = $derived(saveOf(lookup, ...sentFrom.map((k) => (pr.writes.tells(k) ? pr.writes.box(k) : IDLE))));
  const busy = $derived(saving.at === 'saving');
  /**
   * A comment on these lines, sent by a composer closed since, is still
   * being saved (it shows on its lines as that). This composer is for
   * another comment: it takes text, and can save once that one is
   * answered (two saves from one box at once are refused).
   */
  const waits = $derived(sentFrom.some((k) => !pr.writes.tells(k) && pr.writes.box(k).at === 'saving'));
  /** Nothing in it is edited: while it is being saved, and while some of several suggestions are saved and the rest are not. */
  const fixed = $derived(busy || parts !== null);
  /** The boxes whose text and save are on screen here, not beside it or in the toast (see `Writes.tell`). */
  // svelte-ignore state_referenced_locally (the store is the composer's for its life)
  const mine = tellers(pr.writes);
  let gone = false;
  onDestroy(() => {
    gone = true;
    mine.release();
  });
  /** The suggestion's edits, kept beside the draft (the comment's lines can move with them). */
  const editsKey = $derived(`${key}:edits`);
  /**
   * Where a single suggestion moved the comment: its draft and edits are
   * kept there too, so a composer opened on those lines finds them.
   */
  const targetKey = $derived(
    plan?.kind === 'one' && (target.start !== anchor.startLine || target.line !== anchor.line)
      ? `comment:${pr.owner}/${pr.repo}#${pr.number}:${anchor.path}:${anchor.side}:${target.start}-${target.line}`
      : null,
  );
  /** The target key the draft was last copied to, cleared when the target moves on. */
  let mirrored: string | null = null;
  const range = (s: number, e: number) => (s !== e ? `lines ${s}–${e}` : `line ${e}`);
  const lines = $derived(range(target.start, target.line) + (anchor.side === 'LEFT' ? ' (old)' : ''));
  const kind = $derived(
    anchor.route === 'line' ? 'New draft comment' : anchor.route === 'file' ? (anchor.spansHunks ? 'New file comment (across two parts of the diff)' : 'New file comment (outside the diff)') : 'New held comment',
  );
  const note = $derived(
    anchor.route === 'line'
      ? 'Saved to your pending review until you publish.'
      : anchor.route === 'file' && anchor.spansHunks
        ? 'These lines span two parts of the diff, and GitHub takes a line comment only within one: this becomes a file comment in your pending review, naming the lines.'
        : anchor.route === 'file'
        ? 'GitHub takes line comments only inside the diff: this becomes a file comment in your pending review, naming the line.'
        : 'This pull request does not change this file: the comment is held until you publish, then posted to the conversation naming the line.',
  );

  /** Saved edits, kept with the head they were made on. */
  interface SavedEdits {
    v: 1;
    head: string;
    edits: Pick<Region, 'start' | 'end' | 'lines'>[];
  }

  onMount(async () => {
    // Focus at once: keys typed while the saved draft loads are text, never the reader's commands.
    area?.focus();
    // A comment on these lines being sent, or offered for Retry, is not offered again (see Writes.holds).
    const [kept, savedEdits] = await Promise.all([loadDraft(key, { outbox: !pr.writes.holds(key) }), loadDraft(editsKey)]);
    if (gone) return;
    // A comment on these lines that was not saved is told here, with its text, and no longer by the toast.
    const failed = pr.writes.box(key);
    if (failed.at === 'failed') mine.tell(key);
    const saved = failed.at === 'failed' ? mergeText(kept, failed.text ?? '') : kept;
    // Text typed while the saved draft was loading wins.
    if (!body && edited === null) {
      const restored = restoreEdits(savedEdits);
      if (restored !== null) {
        text = parseSuggestion(saved)?.text ?? saved;
        edited = restored;
      } else show(saved || anchor.prefill || '');
    }
    // A span just selected is the one the comment is on, whatever a draft kept here was on.
    if (anchor.span) {
      span = anchor.span;
      saveDraft(key, draftBody);
    }
    if (edited === null) area?.focus();
    loaded = true;
  });

  /** The saved draft is shown: from then on the box may be asked to open a suggested edit. */
  let loaded = $state(false);
  // Opened (or opened again on other lines) to suggest an edit: the suggestion editor shows at once, where these lines take one.
  $effect(() => {
    if (!loaded || !anchor.suggest || !canSuggest) return;
    untrack(() => {
      if (edited === null && head) setEdited(head.join('\n'));
    });
  });

  /** The edited file from saved edits, when they were made on this head. */
  function restoreEdits(json: string): string | null {
    if (!json || !head) return null;
    try {
      const s = JSON.parse(json) as SavedEdits;
      if (s.v !== 1 || s.head !== headHash) return null;
      return applyEdits(head, s.edits).join('\n');
    } catch {
      return null;
    }
  }

  /** Edit `md`: its first suggestion block (on the selected lines) in the suggestion editor, when these lines take one; the rest as the text. */
  function show(md: string) {
    // A kept span shows apart; the box edits the comment after it.
    const split = splitSpan(md);
    if (split.span) span = split.span;
    md = split.text;
    const parsed = canSuggest ? parseSuggestion(md) : null;
    text = parsed ? parsed.text : md;
    const lines = parsed?.lines.map((l) => l.replace(/\r$/, ''));
    edited = lines && head ? applyEdits(head, [{ start: anchor.startLine, end: anchor.line, lines }]).join('\n') : null;
  }

  // While open: a failed comment on these lines given up comes back, beside what was typed since.
  $effect(() => pr.writes.listen(key, (t) => show(mergeText(body, t))));

  function saveEdits() {
    if (edited === null || !head) {
      clearDraft(editsKey);
      return;
    }
    const edits = editRegions(head, suggestionLines(edited)).map(({ start, end, lines }) => ({ start, end, lines }));
    const json = JSON.stringify({ v: 1, head: headHash, edits } satisfies SavedEdits);
    saveDraft(editsKey, json);
    if (mirrored && mirrored !== targetKey) clearMirror();
    if (targetKey) {
      saveDraft(targetKey, draftBody);
      saveDraft(`${targetKey}:edits`, json);
      mirrored = targetKey;
    }
  }

  function clearMirror() {
    if (!mirrored) return;
    clearDraft(mirrored);
    clearDraft(`${mirrored}:edits`);
    mirrored = null;
  }

  function setEdited(next: string | null) {
    edited = next;
    saveDraft(key, draftBody);
    saveEdits();
  }

  function removeSuggestion() {
    setEdited(null);
    area?.focus();
  }

  async function offDiffBody(): Promise<{ body: string; commit: string } | null> {
    const commit = anchor.commit ?? (await anchor.resolveCommit?.().catch(() => null)) ?? null;
    if (!commit) return null;
    const marker = {
      v: 1 as const,
      path: anchor.path,
      line: anchor.line,
      side: anchor.side,
      commit,
      ...(anchor.startLine < anchor.line ? { start: anchor.startLine } : {}),
    };
    const text = buildOffDiffBody({
      marker,
      lines: anchor.text,
      text: body,
      webUrl: session.config?.github_web_url ?? 'https://github.com',
      owner: pr.owner,
      repo: pr.repo,
    });
    return { body: text, commit };
  }

  /** Why this draft can't be saved here: GitHub writes off, or server data off (every draft is held by the server). */
  const offText = $derived(pr.draftsOffText);
  /** Something to post: text, or a suggestion (which may stand alone). */
  const postable = $derived(!!core.trim() || plan?.kind === 'split');

  /**
   * Save the draft. From the press the composer is saving: its button says
   * so and takes no second press, and its text is not edited. Once the
   * server has the draft the composer closes, and the draft is on its
   * lines. If saving fails the composer stays as it was, says why beside
   * the button, and sends again on the next press (the text as it is
   * then). Closed before it is answered, the draft shows on its lines as
   * being saved, and a failure is told by the toast. Edits in several
   * hunks save as one draft per hunk, the comment text with the first
   * (see `parts` for when only some of them are saved).
   */
  /**
   * The agents a new thread here can go to at once (Save & send): those
   * its threads can be sent to (relayAgents.taskAgents, THREAD_NEEDS). Not
   * for a held comment (no thread) or several suggestions (several threads).
   */
  const agents = $derived(!session.agents || anchor.route === 'held' || plan?.kind === 'split' ? [] : relayAgents.taskAgents(pullKey(pr.owner, pr.repo, pr.number), THREAD_NEEDS));
  let pickedAgent = $state('');
  const agent = $derived(agents.find((c) => c.id === pickedAgent) ?? agents[0]);
  /** The agent the press under way sends the new thread to once it is saved (Save & send), if any. */
  let sendTo = '';
  function saveAndSend() {
    if (!agent) return;
    sendTo = agent.id;
    void save();
  }
  /**
   * Sends the thread just saved with `sent` to `to`, with the default
   * request: found among the drafts by its place and text. Its thread
   * opens its Local agent panel (relayAgents.sentTopics), which shows how
   * the delivery went (an unconfirmed one offers Retry there).
   */
  async function sendSaved(to: string, sent: string) {
    const t = [...pr.threads].reverse().find((t) => t.id.startsWith('draft:') && t.path === anchor.path && t.comments.nodes[0]?.body === sent && (anchor.route === 'file' || (t.diffSide === anchor.side && t.line === target.line)));
    if (!t) return;
    try {
      await relayAgents.sendTask(to, threadTopic(pullKey(pr.owner, pr.repo, pr.number), t.id), await threadTask(pr, t, THREAD_REQUEST));
    } catch {
      // The thread's panel says so, with Retry.
    }
  }

  async function save() {
    const then = sendTo;
    sendTo = '';
    // One save per press: a second Ctrl/Cmd+Enter must not post a duplicate.
    if (!(postable || parts) || busy || waits || offText !== null) return;
    /** The first comment's body as posted, to find its thread by (an off-diff one carries its marker). */
    let sent = body;
    // The suggestion editor takes no keys while its edits are sent: the comment text keeps the focus.
    if (edited !== null) area?.focus();
    let sends: Promise<boolean>[];
    if (anchor.route === 'line') {
      const press: readonly Part[] =
        parts ??
        (plan?.kind === 'split'
          ? suggestionComments(plan, text, crlf).map((c, i) => ({ box: boxes[i], start: c.start, line: c.end, body: c.body }))
          : [{ box: key, start: target.start, line: target.line, body }]);
      if (press.length > 1) parts = press;
      sends = press
        .filter((p) => !saved.has(p.box))
        .map((p) => {
          mine.tell(p.box);
          return pr
            .addThread({ path: anchor.path, side: anchor.side, line: p.line, startLine: p.start, body: p.body, subjectType: 'LINE' }, { key: p.box, text: p.body })
            .then((ok) => {
              if (ok) saved = new Set([...saved, p.box]);
              return ok;
            });
        });
      // Sent text carries the suggestion; a failure brings it back from there (see `show`).
      if (edited !== null) clearDraft(editsKey);
      clearMirror();
    } else {
      lookup = { at: 'saving', label: 'Saving draft' };
      let why = 'the commit of these lines is not known yet. Try again in a moment.';
      const off = await offDiffBody().catch((e: unknown) => {
        why = e instanceof Error ? e.message : String(e);
        return null;
      });
      if (gone) return;
      if (!off) {
        lookup = { at: 'failed', label: 'Saving draft', error: { text: why, link: null }, text: null };
        return;
      }
      mine.tell(key);
      sent = off.body;
      sends = [
        anchor.route === 'file'
          ? pr.addFileThread(anchor.path, off.body, { key, text: body })
          : pr.addHeld({ path: anchor.path, line: anchor.line, side: anchor.side, commit_sha: off.commit, body: off.body }, { key, text: body }),
      ];
      // The write is pending from here (`Writes.box`), in the step the lookup ends in.
      lookup = IDLE;
    }
    if (!(await Promise.all(sends)).every(Boolean)) {
      // None of several is saved: nothing is on the server, so nothing is fixed, and the next press sends it all as it is then.
      if (saved.size === 0) parts = null;
      return;
    }
    // Not waited for: the composer closes as the draft is saved; the thread's panel shows the delivery.
    if (then) void sendSaved(then, sent);
    if (!gone) onsaved(focusAt(root));
  }

  // The box's two commands: sending and closing, from its keys (never mid-composition) and from its buttons alike.
  const box = commentBox((c) => {
    if (c === 'submit') void save();
    else void onclose();
  });
  const keys = box.keys;
  const sendKey = $derived(keyboard.keysIn('field', { kind: 'submit' }));
  const closeKey = $derived(keyboard.keysIn('field', { kind: 'cancel' }));

  const outsideLines = $derived(plan?.kind === 'outside' ? plan.outside.map((r) => range(r.start, Math.max(r.start, r.end))).join(', ') : '');
  const saveLabel = $derived(
    anchor.route === 'held'
      ? 'Hold draft'
      : plan?.kind === 'split'
        ? `Add ${plan.suggestions.length} suggestions`
        : plan?.kind === 'outside'
          ? 'Add draft with patch'
          : 'Add draft',
  );
</script>

<div bind:this={root} class="composer" data-composer data-route={anchor.route}>
  <div class="row"><span class="where">{kind} · {anchor.path} · {lines}</span></div>
  {#if span}
    <SpanQuote path={anchor.path} {span}>
      {#snippet actions()}
        <button
          class="micro"
          disabled={fixed}
          title="Comment on the whole lines instead"
          onclick={() => {
            span = null;
            saveDraft(key, draftBody);
            area?.focus();
          }}>Remove quote</button
        >
      {/snippet}
    </SpanQuote>
  {/if}
  <textarea
    bind:this={area}
    bind:value={text}
    readonly={fixed}
    oninput={() => {
      saveDraft(key, draftBody);
      if (targetKey) saveDraft(targetKey, draftBody);
    }}
    {@attach keys}
    aria-label="Comment"
    placeholder="Leave a comment{sendKey || closeKey ? ` (${[sendKey && `${sendKey} to save as draft`, closeKey && `${closeKey} to cancel`].filter(Boolean).join(', ')})` : ''}"
  ></textarea>
  {#if edited !== null && head && plan}
    <!-- Its edits are not changed while they are sent. -->
    <div style="display:contents" inert={fixed}>
    <SuggestionEditor
      path={anchor.path}
      {head}
      selected={{ start: anchor.startLine, end: anchor.line }}
      hunks={anchor.file?.hunks ?? []}
      spans={anchor.file?.spans ?? null}
      text={edited}
      {plan}
      onchange={setEdited}
      onsubmit={save}
      onescape={() => area?.focus()}
      onremove={removeSuggestion}
    />
    </div>
    {#if plan.kind === 'split'}
      <p class="suggest-notice" role="status">
        These edits fall in {plan.suggestions.length} separate parts of the diff, and a GitHub suggestion covers lines in one. They are added as
        {plan.suggestions.length} suggestions, one comment each; your comment text goes with the first.
      </p>
    {:else if plan.kind === 'outside'}
      <p class="suggest-notice warn" role="status">
        {outsideLines.includes(',') ? 'Edits on' : 'The edit on'}
        {outsideLines} {outsideLines.includes(',') ? 'are' : 'is'} outside this pull request's diff, and GitHub takes suggestions only on lines in the
        diff. Add {plan.regions.length === 1 ? 'it' : 'the edits'} as a patch in this comment instead, or undo {outsideLines.includes(',')
          ? 'those edits'
          : 'that edit'} to make a suggestion.
      </p>
    {/if}
  {/if}
  {#if parts && !busy}
    {@const done = parts.filter((p) => saved.has(p.box)).length}
    {@const left = parts.length - done}
    <p class="suggest-notice warn" role="status" data-parts>
      {done} of {parts.length} suggestions {done === 1 ? 'is' : 'are'} saved. Retry sends the {left === 1 ? 'other' : `other ${left}`} as {left === 1 ? 'it was' : 'they were'} written; the comment
      and the edits are not changed until then. Close keeps the {done === 1 ? 'one' : `${done}`} saved and leaves the {left === 1 ? 'other' : 'others'} to the notice that follows.
    </p>
  {/if}
  <div class="row">
    {#if canSuggest}
      {#if edited === null}
        <button class="micro" disabled={fixed} onclick={() => setEdited(head!.join('\n'))} title="Suggest new text: edit the file, starting at the selected lines">± Suggest edit</button>
      {/if}
    {:else}
      <span class="where composer-note">{note}</span>
    {/if}
    <span class="spacer"></span>
    <!-- Closing does not stop a save under way: the draft then shows on its lines as being saved. -->
    <button class="micro" onclick={() => box.send({ kind: 'cancel' })}>{busy ? 'Close' : 'Cancel'}</button>
    <SaveButton
      save={saving}
      label={saveLabel}
      disabled={!(postable || parts) || offText !== null || waits}
      title={offText ?? (waits ? 'Your earlier comment on these lines is still being saved.' : undefined)}
      onsave={() => box.send({ kind: 'submit' })}
    />
    {#if agent}
      <!-- Saves the draft as Add does, then sends its thread to the agent: no second step on the thread. -->
      <span class="save-send">
        <button class="micro" disabled={!postable || busy || offText !== null || waits} title="Save this draft, then send its thread to {agent.name} on your computer" onclick={saveAndSend}>Save &amp; send to {agent.name}</button>
        {#if agents.length > 1}
          <select class="micro" aria-label="Agent to send to" value={agent.id} onchange={(e) => (pickedAgent = e.currentTarget.value)}>
            {#each agents as c (c.id)}<option value={c.id}>{c.name}</option>{/each}
          </select>
        {/if}
      </span>
    {/if}
  </div>
</div>
